import { BranchTag } from "@/components/BranchTag";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, PackageCheck, Link2, Printer, Eye, MoreVertical, Search, SlidersHorizontal, ChevronLeft, ChevronRight, FileText, CalendarDays, Building2, Boxes, CheckCircle2, IndianRupee, GitBranch, X } from "lucide-react";
import { api } from "@/lib/api";
import { openPdf } from "@/lib/print";
import { ProjectFilter, useProjectFilter } from "@/components/procurement/ProjectFilter";
import { usePermissions } from "@/lib/permissions";
import { useCompany } from "@/context/CompanyContext";
import { PageBreadcrumb } from "@/components/PageBreadcrumb";
import { EmptyState } from "@/components/EmptyState";
import { DocumentFlowDialog } from "@/components/DocumentFlowDialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { fmtNum, fmtMoney, fmtDate } from "@/lib/format";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { getErrorMessage } from "@/lib/errors";
import { CompanyField } from "@/components/CompanyField";

// grn-design: pill tabs + search/filter + checkbox table + pagination + detail dialog; "Yet to receive" tab (yet-to-receive)
const PAGE_SIZES = [10, 25, 50];
const TH = "h-11 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground";
const GRN_PILL = {
  POSTED: { dot: "bg-emerald-500", cls: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/30", label: "Posted" },
  DRAFT: { dot: "bg-slate-400", cls: "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-500/10 dark:text-slate-300 dark:border-slate-500/30", label: "Draft" },
  CANCELLED: { dot: "bg-rose-500", cls: "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-500/10 dark:text-rose-400 dark:border-rose-500/30", label: "Cancelled" },
  NOT_RECEIVED: { dot: "bg-amber-500", cls: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/30", label: "Not received" },
  PARTIAL: { dot: "bg-sky-500", cls: "bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-500/10 dark:text-sky-400 dark:border-sky-500/30", label: "Partly received" },
};
function GrnPill({ value }) {
  const c = GRN_PILL[value] || GRN_PILL.DRAFT;
  return <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-medium ${c.cls}`}><span className={`h-1.5 w-1.5 rounded-full ${c.dot}`} />{c.label}</span>;
}
const timeOf = (iso) => {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true }).toLowerCase() : "";
};
const dayKey = (iso) => String(iso || "").slice(0, 10);

export default function GoodsReceiptNotes() {
  const pf = useProjectFilter();
  const { can } = usePermissions();
  const canCreate = can("GRN", "create");
  const canPost = can("GRN", "post");   // post and add-to-stock are both "Post" on the server
  const { companies } = useCompany() || {};
  const [grns, setGrns] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [projects, setProjects] = useState([]);
  const [rec, setRec] = useState({});
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [tab, setTab] = useState("ALL");
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ from: "", to: "", warehouse: "" });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selected, setSelected] = useState([]);
  const [receivingPo, setReceivingPo] = useState(null);
  const [flow, setFlow] = useState(null);
  const flowBtn = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [grnRes, poRes, whRes, itemRes] = await Promise.all([
        api.get("/procurement/grns"), api.get("/procurement/purchase-orders"), api.get("/inventory/warehouses"),
        api.get("/inventory/items").catch(() => ({ data: [] })),
      ]);
      setInventoryItems(itemRes.data);
      setGrns(grnRes.data);
      setPurchaseOrders(poRes.data);
      setWarehouses(whRes.data);
      api.get("/procurement/po-receipts").then(({ data }) => setRec(Object.fromEntries((data || []).map((r) => [r.po_id, r])))).catch(() => {});
      api.get("/procurement/vendors").then(({ data }) => setVendors(data || [])).catch(() => {});
      api.get("/projects").then(({ data }) => setProjects(data || [])).catch(() => {});
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { setPage(1); }, [tab, search, filters, pageSize, pf.value]);
  useEffect(() => { if (flow) flowBtn.current?.click(); }, [flow]);  // opens the Document-flow dialog from the ⋮ menu

  const poOf = (id) => purchaseOrders.find((p) => p.id === id);
  const poNumber = (id) => poOf(id)?.number || "—";
  const vendorName = (poId) => vendors.find((v) => v.id === poOf(poId)?.vendor_id)?.name || "—";
  const whName = (id) => (id ? warehouses.find((w) => w.id === id)?.name || "Warehouse" : "Direct to site");

  const scoped = pf.apply(grns, (r) => r.project_id);
  const count = (st) => scoped.filter((g) => g.status === st).length;
  const yetRows = useMemo(() => [...pf.apply(purchaseOrders.filter((p) => p.status === "APPROVED" && ["NOT_RECEIVED", "PARTIAL"].includes(rec[p.id]?.status)), (r) => r.project_id)]
    .sort((a, b) => String(a.po_date).localeCompare(String(b.po_date))), [purchaseOrders, rec, pf.value]);
  const tabs = [
    { key: "ALL", label: "All", n: scoped.length }, { key: "DRAFT", label: "Draft", n: count("DRAFT") },
    { key: "POSTED", label: "Posted", n: count("POSTED") }, { key: "CANCELLED", label: "Cancelled", n: count("CANCELLED") },
    { key: "YET", label: "Yet to receive", n: yetRows.length },
  ];

  const q = search.trim().toLowerCase();
  const shown = useMemo(() => {
    const hit = (...vals) => !q || vals.some((v) => String(v || "").toLowerCase().includes(q));
    if (tab === "YET") {
      return yetRows.filter((p) => hit(p.number, vendors.find((v) => v.id === p.vendor_id)?.name, p.po_date, ...(p.items || []).map((i) => i.description)));
    }
    return scoped.filter((g) => {
      if (tab !== "ALL" && g.status !== tab) return false;
      if (filters.from && g.grn_date < filters.from) return false;
      if (filters.to && g.grn_date > filters.to) return false;
      if (filters.warehouse === "none" ? g.warehouse_id : filters.warehouse && g.warehouse_id !== filters.warehouse) return false;
      const po = poOf(g.purchase_order_id);
      return hit(g.number, po?.number, vendorName(g.purchase_order_id), g.grn_date, fmtDate(g.grn_date), ...(po?.items || []).map((i) => i.description));
    });
  }, [tab, q, scoped, yetRows, filters, purchaseOrders, vendors]);

  const pages = Math.max(1, Math.ceil(shown.length / pageSize));
  const cur = Math.min(page, pages);
  const rows = shown.slice((cur - 1) * pageSize, cur * pageSize);
  const from = shown.length ? (cur - 1) * pageSize + 1 : 0;
  const filtersOn = Boolean(filters.from || filters.to || filters.warehouse);
  const allOnPage = tab !== "YET" && rows.length > 0 && rows.every((g) => selected.includes(g.id));
  const toggleAll = () => setSelected(allOnPage ? selected.filter((id) => !rows.some((g) => g.id === id)) : [...new Set([...selected, ...rows.map((g) => g.id)])]);

  const post = async (grn) => {
    setBusyId(grn.id);
    try {
      await api.post(`/procurement/grns/${grn.id}/post`);
      toast.success("GRN posted to GL (Inventory / GRIR)" + (grn.warehouse_id ? " and stock received" : ""));
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const addToStock = async (grn) => {
    setBusyId(grn.id);
    try {
      const { data } = await api.post(`/procurement/grns/${grn.id}/receive-stock`);
      if (data.received?.length) toast.success(`Added to stock: ${data.received.join(", ")}`);
      else if (!data.skipped?.length) toast.info("This GRN is already in stock — nothing to add");
      (data.skipped || []).forEach((m) => toast.warning(`Not added: ${m}`));
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const pagerBtn = "grid h-9 w-9 place-items-center rounded-lg border border-border text-muted-foreground hover:bg-accent disabled:opacity-40";
  return (
    <div className="space-y-5" data-testid="grns-page">
      <div className="space-y-2">
        <PageBreadcrumb crumbs={[{ label: "Operations" }, { label: "Goods Receipt Notes" }]} />
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold tracking-tight text-foreground">Goods Receipt Notes</h1>
            <p className="mt-1 max-w-3xl text-sm text-muted-foreground">Record goods received against an approved PO. Posting creates a Dr Inventory / Cr GRIR journal entry.</p>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <ProjectFilter filter={pf} />
            {canCreate && <CreateGRNDialog companies={companies} purchaseOrders={purchaseOrders.filter((p) => p.status === "APPROVED")} grns={grns} warehouses={warehouses} inventoryItems={inventoryItems} onCreated={load} presetPo={receivingPo} onPresetDone={() => setReceivingPo(null)} />}
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card p-4 shadow-sm sm:p-5">
        <div className="flex flex-wrap gap-2" role="tablist" data-testid="grn-tabs">
          {tabs.map((t) => {
            const on = tab === t.key;
            return (
              <button key={t.key} type="button" role="tab" aria-selected={on} onClick={() => setTab(t.key)} data-testid={`grn-tab-${t.key}`}
                className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${on ? "border-[#910029] bg-[#910029] text-white" : "border-border bg-card text-foreground hover:bg-accent"}`}>
                {t.label} ({t.n})
              </button>
            );
          })}
        </div>

        <div className="mt-4 flex items-center gap-2.5" data-testid="grn-toolbar">
          <div className="relative min-w-0 flex-1">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input data-testid="grn-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by GRN no., PO no., vendor, description or date..."
              className="h-11 w-full rounded-lg border border-border bg-background pl-10 pr-3 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:border-[#910029] focus:ring-2 focus:ring-[#910029]/20" />
          </div>
          <Popover>
            <PopoverTrigger asChild>
              <button type="button" title="Filters" data-testid="grn-filter-button" className="relative grid h-11 w-11 shrink-0 place-items-center rounded-lg border border-border bg-background text-foreground hover:bg-accent">
                <SlidersHorizontal className="h-4 w-4" />
                {filtersOn && <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-[#910029]" />}
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72 space-y-3">
              <p className="text-sm font-semibold text-foreground">Filter GRNs</p>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs">From</Label><Input type="date" value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })} className="mt-1 h-9" data-testid="grn-filter-from" /></div>
                <div><Label className="text-xs">To</Label><Input type="date" value={filters.to} onChange={(e) => setFilters({ ...filters, to: e.target.value })} className="mt-1 h-9" data-testid="grn-filter-to" /></div>
              </div>
              <div>
                <Label className="text-xs">Received into</Label>
                <select value={filters.warehouse} onChange={(e) => setFilters({ ...filters, warehouse: e.target.value })} data-testid="grn-filter-warehouse" className="mt-1 h-9 w-full rounded-md border border-input bg-background px-2 text-sm">
                  <option value="">All</option>
                  <option value="none">Direct to site (no stock)</option>
                  {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                </select>
              </div>
              {filtersOn && <Button type="button" variant="outline" size="sm" onClick={() => setFilters({ from: "", to: "", warehouse: "" })} data-testid="grn-filter-clear"><X className="h-3.5 w-3.5" /> Clear filters</Button>}
            </PopoverContent>
          </Popover>
        </div>

        <div className="mt-4 overflow-hidden rounded-xl border border-border">
          {!loading && shown.length === 0 ? (
            tab === "YET"
              ? <EmptyState title="Nothing waiting for delivery" description="Every approved purchase order has been received in full." testId="grn-yet-empty" />
              : <EmptyState title={grns.length === 0 ? "No GRNs yet" : "No GRNs match"} description={grns.length === 0 ? "Approve a Purchase Order first, then receive goods against it here." : "Try another tab, search or filter."} testId="grns-empty-state" />
          ) : tab === "YET" ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow className="bg-secondary/60 hover:bg-secondary/60">
                  <TableHead className={`${TH} pl-5`}>PO No.</TableHead><TableHead className={TH}>Vendor</TableHead><TableHead className={TH}>Ordered</TableHead><TableHead className={TH}>Pending</TableHead><TableHead className={TH}>Status</TableHead><TableHead className={`${TH} pr-5 text-right`}>Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {rows.map((po) => {
                    const r = rec[po.id];
                    const d = Math.max(Math.floor((Date.now() - new Date(po.po_date).getTime()) / 86400000), 0);
                    const open = (po.items || []).filter((it) => (r.items[it.id]?.pending ?? Number(it.quantity)) > 0);
                    return (
                      <TableRow key={po.id} className="h-[72px]" data-testid={`grn-yet-${po.number}`}>
                        <TableCell className="pl-5 text-sm font-medium text-foreground">{po.number}</TableCell>
                        <TableCell className="text-sm text-foreground">{vendors.find((v) => v.id === po.vendor_id)?.name || "—"}</TableCell>
                        <TableCell className="text-sm text-foreground"><div>{fmtDate(po.po_date)}</div><div className="text-xs text-muted-foreground">{d} day{d === 1 ? "" : "s"} ago</div></TableCell>
                        <TableCell className="text-xs">
                          {open.slice(0, 2).map((it) => <div key={it.id} className="text-foreground">{it.description} — <b className="text-amber-600 dark:text-amber-400">{fmtNum(r.items[it.id]?.pending ?? it.quantity, 3)} pending</b> <span className="text-muted-foreground">of {fmtNum(it.quantity, 3)}</span></div>)}
                          {open.length > 2 && <div className="text-muted-foreground">+ {open.length - 2} more</div>}
                          {r.draft_grns > 0 && <div className="text-muted-foreground">{r.draft_grns} GRN in draft — post it to count it</div>}
                        </TableCell>
                        <TableCell><GrnPill value={r.status} /></TableCell>
                        <TableCell className="pr-5 text-right">
                          {canCreate && <Button size="sm" onClick={() => setReceivingPo(po)} data-testid={`grn-receive-${po.number}`}><PackageCheck className="h-3.5 w-3.5" /> Receive goods</Button>}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow className="bg-secondary/60 hover:bg-secondary/60">
                  <TableHead className="h-11 w-12 pl-5"><Checkbox checked={allOnPage} onCheckedChange={toggleAll} aria-label="Select all" data-testid="grn-select-all" /></TableHead>
                  <TableHead className={TH}>GRN No.</TableHead><TableHead className={TH}>PO No.</TableHead><TableHead className={TH}>Vendor</TableHead>
                  <TableHead className={TH}>Items</TableHead><TableHead className={TH}>Received On</TableHead><TableHead className={TH}>Status</TableHead><TableHead className={`${TH} pr-5 text-right`}>Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {rows.map((grn) => (
                    <TableRow key={grn.id} className="h-[72px]" data-testid={`grn-card-${grn.number}`} data-state={selected.includes(grn.id) ? "selected" : undefined}>
                      <TableCell className="pl-5"><Checkbox checked={selected.includes(grn.id)} onCheckedChange={(v) => setSelected(v ? [...selected, grn.id] : selected.filter((id) => id !== grn.id))} aria-label={`Select ${grn.number}`} /></TableCell>
                      <TableCell className="text-sm font-medium text-foreground">{grn.number}<BranchTag row={grn} /></TableCell>
                      <TableCell className="text-sm text-muted-foreground">{poNumber(grn.purchase_order_id)}</TableCell>
                      <TableCell className="text-sm text-foreground">{vendorName(grn.purchase_order_id)}</TableCell>
                      <TableCell className="text-sm text-foreground">{(grn.items || []).length}</TableCell>
                      <TableCell className="text-sm text-foreground"><div>{fmtDate(grn.grn_date)}</div><div className="text-xs text-muted-foreground">{timeOf(grn.created_at)}</div></TableCell>
                      <TableCell><GrnPill value={grn.status} /></TableCell>
                      <TableCell className="pr-5">
                        <div className="flex items-center justify-end gap-2">
                          <Button size="sm" variant="outline" onClick={() => setViewing(grn)} data-testid={`grn-view-button-${grn.number}`}><Eye className="h-3.5 w-3.5" /> View</Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild><button type="button" title="More" data-testid={`grn-more-${grn.number}`} className="grid h-9 w-9 place-items-center rounded-lg border border-border bg-background text-muted-foreground hover:bg-accent hover:text-foreground"><MoreVertical className="h-4 w-4" /></button></DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                              <DropdownMenuItem onSelect={() => openPdf(`/print/grns/${grn.id}`)} data-testid={`grn-print-button-${grn.number}`}><Printer className="mr-2 h-4 w-4" /> Print / PDF</DropdownMenuItem>
                              {canPost && grn.status === "DRAFT" && <DropdownMenuItem disabled={busyId === grn.id} onSelect={() => post(grn)} data-testid={`post-grn-button-${grn.number}`}><PackageCheck className="mr-2 h-4 w-4" /> Post</DropdownMenuItem>}
                              {canPost && grn.status === "POSTED" && grn.warehouse_id && <DropdownMenuItem disabled={busyId === grn.id} onSelect={() => addToStock(grn)} data-testid={`grn-add-stock-button-${grn.number}`}><PackageCheck className="mr-2 h-4 w-4" /> Add to stock</DropdownMenuItem>}
                              <DropdownMenuItem onSelect={() => setFlow({ grn, n: Date.now() })} data-testid={`grn-flow-${grn.number}`}><GitBranch className="mr-2 h-4 w-4" /> Document flow{grn.status === "POSTED" ? " / cancel" : ""}</DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3" data-testid="grn-pager">
          <p className="text-sm text-muted-foreground">Showing {from} – {from ? from + rows.length - 1 : 0} of {shown.length}{selected.length > 0 && tab !== "YET" ? ` · ${selected.length} selected` : ""}</p>
          <div className="flex items-center gap-2">
            <button type="button" disabled={cur <= 1} onClick={() => setPage(cur - 1)} aria-label="Previous page" className={pagerBtn}><ChevronLeft className="h-4 w-4" /></button>
            {Array.from({ length: pages }, (_, i) => i + 1).slice(Math.max(0, cur - 3), Math.max(0, cur - 3) + 5).map((n) => (
              <button key={n} type="button" onClick={() => setPage(n)} aria-current={n === cur ? "page" : undefined}
                className={`grid h-9 w-9 place-items-center rounded-lg border text-sm font-semibold ${n === cur ? "border-[#910029] bg-[#910029] text-white" : "border-border text-muted-foreground hover:bg-accent"}`}>{n}</button>
            ))}
            <button type="button" disabled={cur >= pages} onClick={() => setPage(cur + 1)} aria-label="Next page" className={pagerBtn}><ChevronRight className="h-4 w-4" /></button>
            <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
              <SelectTrigger className="h-9 w-[7.5rem] text-sm" data-testid="grn-page-size"><SelectValue /></SelectTrigger>
              <SelectContent>{PAGE_SIZES.map((n) => <SelectItem key={n} value={String(n)}>{n} / page</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <ViewGRNDialog grn={viewing} onClose={() => setViewing(null)} purchaseOrders={purchaseOrders} grns={grns} warehouses={warehouses} vendorName={vendorName} />
      {flow && (
        <DocumentFlowDialog key={flow.n} docType="GRN" docId={flow.grn.id} cancelEndpoint={flow.grn.status === "POSTED" ? `/procurement/grns/${flow.grn.id}/cancel` : undefined} onCancelled={() => { setFlow(null); load(); }}
          trigger={<button ref={flowBtn} type="button" className="hidden" aria-hidden tabIndex={-1} />} />
      )}
    </div>
  );
}

function InfoCell({ icon: Icon, label, children }) {
  return (
    <div className="flex min-w-0 items-center gap-3">
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-[#F5E6EC] text-[#910029] dark:bg-[#4E2634] dark:text-[#F0899F]"><Icon className="h-4 w-4" /></span>
      <div className="min-w-0"><p className="text-[11px] text-muted-foreground">{label}</p><p className="break-words text-sm font-medium leading-snug text-foreground">{children}</p></div>
    </div>
  );
}

function ViewGRNDialog({ grn, onClose, purchaseOrders, grns, warehouses, vendorName }) {
  const po = grn ? purchaseOrders.find((p) => p.id === grn.purchase_order_id) : null;
  const wh = grn?.warehouse_id ? warehouses.find((w) => w.id === grn.warehouse_id) : null;
  const poLine = (id) => (po?.items || []).find((i) => i.id === id);
  const allAssets = !wh && (grn?.items || []).length > 0 && (grn.items || []).every((i) => poLine(i.po_item_id)?.is_capital);   // machines / assets never go to stock
  // Received up to and including this GRN (cancelled GRNs never count), so Pending is what was still owed after it.
  const receivedThrough = (poItemId) => (grns || []).filter((g) => g.status !== "CANCELLED" && (g.id === grn.id || String(g.created_at) <= String(grn.created_at)))
    .flatMap((g) => g.items || []).filter((i) => i.po_item_id === poItemId).reduce((s, i) => s + Number(i.quantity_received || 0), 0);
  const lines = (grn?.items || []).map((it) => {
    const line = poLine(it.po_item_id); const ordered = Number(line?.quantity || 0); const rate = Number(line?.rate || 0); const got = Number(it.quantity_received || 0);
    return { id: it.id || it.po_item_id, description: line?.description || "—", ordered, got, pending: Math.max(ordered - (grn.status === "CANCELLED" ? got : receivedThrough(it.po_item_id)), 0), rate, value: got * rate };
  });
  const totalQty = lines.reduce((s, l) => s + l.got, 0);
  const totalValue = lines.reduce((s, l) => s + l.value, 0);
  const when = grn ? `${fmtDate(grn.grn_date)}${timeOf(grn.created_at) ? `, ${timeOf(grn.created_at)}` : ""}` : "";
  return (
    <Dialog open={!!grn} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-4xl" data-testid="grn-view-dialog">
        {grn && (<>
          <DialogHeader>
            <div className="flex items-center gap-3">
              <span className="grid h-11 w-11 place-items-center rounded-xl border border-border bg-card text-[#910029]"><FileText className="h-5 w-5" /></span>
              <div>
                <DialogTitle className="text-lg">Goods Receipt Note</DialogTitle>
                <div className="mt-1 flex items-center gap-2 text-sm text-foreground"><span className="font-medium">{grn.number}</span><GrnPill value={grn.status} /></div>
              </div>
            </div>
          </DialogHeader>
          <div className="grid gap-4 rounded-xl border border-border bg-secondary/30 p-4 sm:grid-cols-2 md:grid-cols-[auto_auto_1fr_auto]">
            <InfoCell icon={CalendarDays} label="Date">{when}</InfoCell>
            <InfoCell icon={FileText} label="Purchase Order">{po?.number || "—"}</InfoCell>
            <InfoCell icon={Building2} label="Vendor">{vendorName(grn.purchase_order_id)}</InfoCell>
            <InfoCell icon={Boxes} label="Received Into">{wh ? wh.name : allAssets ? "Fixed assets (not stock)" : "Direct to site (no stock)"}</InfoCell>
          </div>
          <div className="overflow-hidden rounded-xl border border-border">
            <p className="bg-secondary/30 px-4 py-2.5 text-sm font-semibold text-foreground">Items Received</p>
            <div className="max-h-[40vh] overflow-auto">
              <Table>
                <TableHeader><TableRow className="bg-secondary/60 hover:bg-secondary/60">
                  <TableHead className={`${TH} h-9 pl-4`}>#</TableHead><TableHead className={`${TH} h-9`}>Description</TableHead>
                  <TableHead className={`${TH} h-9 text-right`}>Ordered</TableHead><TableHead className={`${TH} h-9 text-right`}>Received</TableHead><TableHead className={`${TH} h-9 text-right`}>Pending</TableHead>
                  <TableHead className={`${TH} h-9 text-right`}>Rate (₹)</TableHead><TableHead className={`${TH} h-9 pr-4 text-right`}>Total Received (₹)</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {lines.map((l, i) => (
                    <TableRow key={l.id}>
                      <TableCell className="pl-4 text-sm">{i + 1}</TableCell><TableCell className="text-sm">{l.description}</TableCell>
                      <TableCell className="text-right text-sm tabular-nums">{fmtNum(l.ordered, 3)}</TableCell><TableCell className="text-right text-sm tabular-nums">{fmtNum(l.got, 3)}</TableCell>
                      <TableCell className={`text-right text-sm tabular-nums ${l.pending > 0 ? "font-semibold text-amber-600 dark:text-amber-400" : ""}`}>{fmtNum(l.pending, 3)}</TableCell>
                      <TableCell className="text-right text-sm tabular-nums">{fmtMoney(l.rate)}</TableCell><TableCell className="pr-4 text-right text-sm font-medium tabular-nums">{fmtMoney(l.value)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
          <div className="grid divide-y divide-border rounded-xl border border-border bg-secondary/30 sm:grid-cols-3 sm:divide-x sm:divide-y-0" data-testid="grn-summary">
            <div className="flex items-center gap-3 p-4"><span className="grid h-9 w-9 place-items-center rounded-full bg-emerald-500/15 text-emerald-600"><CheckCircle2 className="h-5 w-5" /></span><div><p className="text-[11px] text-muted-foreground">Total Received</p><p className="text-lg font-semibold tabular-nums text-foreground">{fmtNum(totalQty, 3)}</p></div></div>
            <div className="flex items-center gap-3 p-4"><span className="grid h-9 w-9 place-items-center rounded-full bg-[#F5E6EC] text-[#910029] dark:bg-[#4E2634] dark:text-[#F0899F]"><IndianRupee className="h-4 w-4" /></span><div><p className="text-[11px] text-muted-foreground">Total Value</p><p className="text-lg font-semibold tabular-nums text-foreground">₹ {fmtMoney(totalValue)}</p></div></div>
            <div className="flex items-center gap-3 p-4"><span className="grid h-9 w-9 place-items-center rounded-full bg-secondary text-muted-foreground"><FileText className="h-4 w-4" /></span><div><p className="text-[11px] text-muted-foreground">Status</p><GrnPill value={grn.status} /></div></div>
          </div>
          {grn.remarks && <p className="text-sm"><span className="text-muted-foreground">Remarks: </span>{grn.remarks}</p>}
          <DialogFooter>
            <Button variant="outline" onClick={onClose}>Close</Button>
            <Button onClick={() => openPdf(`/print/grns/${grn.id}`)} data-testid="grn-view-print-button"><Printer className="h-4 w-4" /> Print / PDF</Button>
          </DialogFooter>
        </>)}
      </DialogContent>
    </Dialog>
  );
}

function CreateGRNDialog({ companies, purchaseOrders, grns, warehouses, inventoryItems, onCreated, presetPo, onPresetDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ company_id: "", purchase_order_id: "", warehouse_id: "", grn_date: "" });
  const [quantities, setQuantities] = useState({});
  const [lineItems, setLineItems] = useState({});
  const [saving, setSaving] = useState(false);
  const [materialsByWbsId, setMaterialsByWbsId] = useState({});
  const stockItems = (inventoryItems || []).filter((i) => i.category === "STOCK" && i.is_active !== false);
  const norm = (t) => String(t || "").toLowerCase().replace(/-/g, " ").split(/\s+/).filter(Boolean).join(" ");
  const guessItem = (poLine) => {
    if (poLine.item_id) return poLine.item_id;
    const d = norm(poLine.description);
    const exact = stockItems.filter((i) => norm(i.name) === d || norm(i.code) === d);
    if (exact.length === 1) return exact[0].id;
    const partial = stockItems.filter((i) => d.includes(norm(i.name)) || norm(i.name).includes(d));
    return partial.length === 1 ? partial[0].id : "";
  };
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const selectedPO = purchaseOrders.find((p) => p.id === form.purchase_order_id);
  const siteStore = selectedPO?.project_id ? (warehouses || []).find((w) => w.project_id === selectedPO.project_id && w.is_active !== false) : null;

  // Quantity already received on each PO line (all GRNs except cancelled ones)
  const receivedSoFar = (poItemId) =>
    (grns || [])
      .filter((g) => g.status !== "CANCELLED")
      .flatMap((g) => g.items || [])
      .filter((i) => i.po_item_id === poItemId)
      .reduce((sum, i) => sum + Number(i.quantity_received || 0), 0);
  const pendingQty = (item) => Math.max(Number(item.quantity) - receivedSoFar(item.id), 0);

  const selectPO = (id) => {
    const po = purchaseOrders.find((p) => p.id === id);
    setForm({ ...form, purchase_order_id: id, company_id: po?.company_id || form.company_id });
    setQuantities(Object.fromEntries((po?.items || []).map((it) => [it.id, pendingQty(it) ? String(+pendingQty(it).toFixed(3)) : ""])));
    setLineItems(Object.fromEntries((po?.items || []).map((it) => [it.id, guessItem(it)])));
    setMaterialsByWbsId({});
    const wbsIds = (po?.items || []).map((it) => it.wbs_item_id).filter(Boolean);
    if (po?.project_id && wbsIds.length > 0) {
      api.get(`/projects/${po.project_id}/materials`)
        .then(({ data }) => setMaterialsByWbsId(Object.fromEntries((data || []).map((m) => [m.id, m.material || {}]))))
        .catch(() => {});
    }
  };

  useEffect(() => { if (presetPo) { setOpen(true); selectPO(presetPo.id); } }, [presetPo?.id]);  // opened from the Yet-to-receive tab

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const items = Object.entries(quantities).filter(([, qty]) => qty).map(([po_item_id, quantity_received]) => ({ po_item_id, quantity_received, item_id: lineItems[po_item_id] || null }));
      await api.post("/procurement/grns", { ...form, warehouse_id: form.warehouse_id || null, items });
      toast.success("GRN created");
      setForm({ company_id: "", purchase_order_id: "", warehouse_id: "", grn_date: "" });
      setQuantities({});
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) onPresetDone?.(); }}>
      <DialogTrigger asChild><Button data-testid="create-grn-button" data-tour="grn-new"><Plus className="h-4 w-4" /> New GRN</Button></DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>New Goods Receipt Note</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3.5" data-testid="create-grn-form">
          <div className="grid grid-cols-2 gap-3">
            <CompanyField label="Company" value={form.company_id} onChange={(v) => setForm({ ...form, company_id: v })} testId="grn-company-select" />
            <div><Label>Date</Label><Input required type="date" data-testid="grn-date-input" value={form.grn_date} onChange={set("grn_date")} className="mt-1.5" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Purchase Order</Label>
              <Select required value={form.purchase_order_id} onValueChange={selectPO}>
                <SelectTrigger data-testid="grn-po-select" className="mt-1.5"><SelectValue placeholder="Select an approved PO" /></SelectTrigger>
                <SelectContent>{purchaseOrders.map((p) => <SelectItem key={p.id} value={p.id}>{p.number}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Destination <span className="font-normal text-muted-foreground">(a warehouse, or this project's site store)</span></Label>
              <Select value={form.warehouse_id} onValueChange={(v) => setForm({ ...form, warehouse_id: v })}>
                <SelectTrigger data-testid="grn-warehouse-select" className="mt-1.5"><SelectValue placeholder="Raise stock on posting" /></SelectTrigger>
                <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}{w.project_id ? " · site store" : ""}</SelectItem>)}</SelectContent>
              </Select>
              {(selectedPO?.items || []).some((i) => i.is_capital) && (
                <p className="mt-1.5 text-xs text-muted-foreground" data-testid="grn-asset-note">
                  Machines and assets on this order are not kept in stock. When the receipt is posted they go straight to <b>Fixed Assets</b>, so leave the destination empty for them.
                </p>
              )}
              {siteStore && form.warehouse_id !== siteStore.id && (
                <button type="button" className="mt-1.5 text-xs text-[#910029] underline" onClick={() => setForm({ ...form, warehouse_id: siteStore.id })} data-testid="grn-use-site-store">Deliver directly to site: {siteStore.name}</button>
              )}
            </div>
          </div>
          {selectedPO && (
            <div className="space-y-2" data-testid="grn-po-items">
              <Label>Quantities received <span className="font-normal text-muted-foreground">(pre-filled with what is still pending — change to what actually arrived)</span></Label>
              {selectedPO.items.map((item) => (
                <div key={item.id} className="space-y-1.5 rounded-lg border border-border p-2.5 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="flex-1 text-muted-foreground">{item.description} (ordered {Number(item.quantity)}, received so far {receivedSoFar(item.id)}, pending {pendingQty(item)})</span>
                    <Input type="number" step="0.001" className="w-28" value={quantities[item.id] || ""} onChange={(e) => setQuantities({ ...quantities, [item.id]: e.target.value })} data-testid={`grn-item-qty-input-${item.id}`} />
                  </div>
                  {item.wbs_item_id && (
                    <div className="flex items-start gap-1.5 rounded-md bg-emerald-500/10 px-2 py-1.5 text-[11px] text-emerald-700 dark:text-emerald-400" data-testid={`grn-material-link-${item.id}`}>
                      <Link2 className="mt-0.5 h-3 w-3 shrink-0" />
                      <span>
                        Linked to Materials DPR — posting this GRN will update <b>Received</b>
                        {materialsByWbsId[item.wbs_item_id] ? (
                          <> from <b>{fmtNum(materialsByWbsId[item.wbs_item_id].qty_received)}</b> to <b>{fmtNum((Number(materialsByWbsId[item.wbs_item_id].qty_received) || 0) + (Number(quantities[item.id]) || 0))}</b> / {fmtNum(materialsByWbsId[item.wbs_item_id].qty_ordered)}, and the delivery date, automatically.</>
                        ) : " automatically."}
                      </span>
                    </div>
                  )}
                  {form.warehouse_id && (
                    <div className="flex items-center gap-2">
                      <span className="w-28 shrink-0 text-[11px] text-muted-foreground">Inventory item</span>
                      <Select value={lineItems[item.id] || "none"} onValueChange={(v) => setLineItems({ ...lineItems, [item.id]: v === "none" ? "" : v })}>
                        <SelectTrigger className={`h-8 min-w-0 flex-1 text-xs ${lineItems[item.id] ? "" : "border-amber-500/60 text-amber-600 dark:text-amber-400"}`} data-testid={`grn-item-inventory-select-${item.id}`}>
                          <SelectValue placeholder="Choose the stock item" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Not linked — won't be added to stock</SelectItem>
                          {stockItems.map((si) => <SelectItem key={si.id} value={si.id}>{si.code} — {si.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              ))}
              {form.warehouse_id && stockItems.length === 0 && (
                <p className="text-xs text-amber-600 dark:text-amber-400">No STOCK items exist yet. Create them in Inventory → Items first, then come back.</p>
              )}
              {!form.warehouse_id && (
                <p className="text-xs text-muted-foreground">No destination chosen: goods are treated as used at once on the project and are not kept as stock. To keep them as site stock, deliver them to the project's site store (Project → Site Inventory).</p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button type="submit" disabled={saving || !selectedPO} data-testid="create-grn-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
